import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiConfig } from "../../config";
import { updateGetCartQueryData } from "../cart";
import { IGetCart } from "../cart/interfaces/cart-interfaces";
import ApiOrder from "./api-order";
import { IGetAllOrdersParams } from "./interfaces/order-interfaces";
import { Keys } from "../../common-interfaces/quiries-keys/keysValues";
import { getKey } from "../../common-interfaces/quiries-keys/queries-keys";
import { getAccessToken } from "@/utils/functions/cookieOperations";

export const useOrder = () => new ApiOrder(apiConfig());
export const useGetAllOrdersQuery = (params: IGetAllOrdersParams) => {
  const order = useOrder();
  return useQuery(
    [...getKey(Keys.get_all_orders), Object.values(params), getAccessToken()],
    () => order.getAllOrders(params),
    {
      refetchOnWindowFocus: false,
    },
  );
};
export const useGetOrderDetailsQuery = (id: number) => {
  const order = useOrder();
  return useQuery(
    [...getKey(Keys.get_order_details), id],
    () => order.getOrderDetails(id),
    {
      refetchOnWindowFocus: false,
    },
  );
};
export const useReOrder = ({
  onSuccess,
  onError,
  orderId,
  params,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
  orderId: number;
  params: IGetAllOrdersParams;
}) => {
  const orders = useOrder();
  const queryClient = useQueryClient();
  const getAllOrders = useGetAllOrdersQuery({ ...params });
  const getOredDetails = useGetOrderDetailsQuery(orderId);
  return useMutation(orders.reorder, {
    onSuccess: (res) => {
      onSuccess(res);
      updateGetCartQueryData(queryClient, res as IGetCart);
      getAllOrders.refetch();
      getOredDetails.refetch();
    },
    onError: onError,
  });
};
export const useCancelOrder = ({
  onSuccess,
  onError,
  orderId,
  params,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
  orderId: number;
  params: IGetAllOrdersParams;
}) => {
  const orders = useOrder();
  const queryClient = useQueryClient();
  const getAllOrders = useGetAllOrdersQuery({ ...params });
  const getOredDetails = useGetOrderDetailsQuery(orderId);
  return useMutation(orders.cancelOrder, {
    onSuccess: (res) => {
      onSuccess(res);
      updateGetCartQueryData(queryClient, res as IGetCart);
      getAllOrders.refetch();
      getOredDetails.refetch();
    },
    onError: onError,
  });
};

export const useGetPaymentLinkQuery = (id: number) => {
  const order = useOrder();
  return useQuery(
    [...getKey(Keys.get_all_orders), id],
    () => order.getPaymentLink(id),
    {
      refetchOnWindowFocus: false,
    },
  );
};
export const usePayOrder = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const order = useOrder();
  return useMutation(order.pay, {
    onSuccess: (res) => {
      onSuccess(res);
    },
    onError: onError,
  });
};
export const useConfirmCod = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const order = useOrder();
  return useMutation(order.confirmCod, {
    onSuccess: (res) => {
      onSuccess(res);
    },
    onError: onError,
  });
};

export const useGetCheckTokenQuery = (token: string) => {
  const order = useOrder();
  return useQuery(
    [...getKey(Keys.checkToken), token],
    () => order.checkToken(token),
    {
      refetchOnWindowFocus: false,
      enabled: !!token,
      retry: false,
    },
  );
};

// export const getAllOrdersSSR = async (
//   queryClient: QueryClient,
//   locale: string,
//   host: string,
//   params: IGetAllOrdersParams
// ) => {
//   const ssrOrder = new ApiOrder(apiConfig(locale, host));
//   await queryClient.prefetchQuery(
//     [Keys.get_all_orders, Object.values(params), locale],
//     () => ssrOrder.getAllOrders(params)
//   );
// };
